/**
 * BondToAtomCalculator - BA Arrow Type
 *
 * Handles arrows from bonds to atoms (particles)
 * Common in bond breaking or leaving group departure
 */

import { v3 } from "@alchemiesolns/maths";
import {
  ArrowCalculator,
  type ArrowCalculationConfig,
  type ArrowEndpoints,
} from "./types";
import {
  defaultBondPositioner,
  type BondPositioner,
} from "../positioning/BondPositioner";
import { calculateParticleDirection, blendDirections } from "../core/geometry";
import { ENDPOINT_CONSTANTS } from "../core/constants";

/**
 * Calculator for Bond-to-Atom (BA) arrows
 *
 * Source: Bond
 * Destination: Atom (particle)
 */
export class BondToAtomCalculator extends ArrowCalculator {
  private bondPositioner: BondPositioner;

  constructor(bondPositioner: BondPositioner = defaultBondPositioner) {
    super();
    this.bondPositioner = bondPositioner;
  }

  getTypeName(): string {
    return "Bond→Atom (BA)";
  }

  calculate(config: ArrowCalculationConfig): ArrowEndpoints {
    const { source, destination, cameraDir } = config;

    // Validate types
    if (source.type !== "bond") {
      throw new Error(
        `BondToAtomCalculator expects source type 'bond', got '${source.type}'`
      );
    }
    if (destination.type !== "particle") {
      throw new Error(
        `BondToAtomCalculator expects destination type 'particle', got '${destination.type}'`
      );
    }

    let endPos = destination.position;
    const bondPos = source.position;

    // Handle fish arrow (shortens to midpoint if not adjacent)
    const fishResult = this.handleFishArrow(config, bondPos, endPos);
    endPos = fishResult.endPos;

    // Step 1: Calculate start position (choose bond end closer to destination)
    let startPos = this.bondPositioner.calculateStartPoint(source, endPos);

    // Step 2: Calculate start direction (perpendicular to bond)
    const initialStartDir = this.bondPositioner.calculateDirection(
      source,
      endPos,
      cameraDir
    );

    // Step 3: Calculate end direction (toward particle)
    let endDir = calculateParticleDirection(endPos, startPos);
    endDir = v3.normalize(v3.addVectors(endDir, initialStartDir));

    // Apply adjacency adjustment
    if (config.isDestAdjacent) {
      const dirRef = v3.normalize(v3.subVectors(startPos, endPos));
      endDir = v3.normalize(v3.addVectors(endDir, dirRef));
    }

    // Step 4: Blend start and end directions for smooth curve
    const startDir = blendDirections(
      initialStartDir,
      endDir,
      ENDPOINT_CONSTANTS.DIRECTION_BLEND_WEIGHT
    );

    // Recalculate start position with blended direction (if needed)
    startPos = this.bondPositioner.calculateStartPoint(source, endPos);

    // Re-blend end direction
    endDir = blendDirections(
      endDir,
      initialStartDir,
      ENDPOINT_CONSTANTS.DIRECTION_BLEND_WEIGHT
    );

    // Step 5: Calculate final endpoint
    const p3 = v3.addScaledVector(
      endPos,
      endDir,
      config.isDestAdjacent
        ? fishResult.destOffset
        : ENDPOINT_CONSTANTS.PARTICLE_GENERIC_OFFSET
    );

    return {
      startPos,
      endPos,
      p3,
      startDir,
      endDir,
      isDestAdjacent: config.isDestAdjacent,
    };
  }
}

/**
 * Default singleton instance
 */
export const defaultBondToAtomCalculator = new BondToAtomCalculator();
